import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { Check, Rocket, Loader2, Sparkles, ShieldCheck, Calendar, Settings } from "lucide-react";
import PlanComparison from "@/components/PlanComparison";

const FEATURES = [
    "Unlimited AI terrain classifications",
    "Live orbital GIS map & ice overlay",
    "Unlimited research projects & map pins",
    "Subsurface telemetry & charts",
    "Full guided learning path",
    "Exportable scientific reports",
];

export default function Plans() {
    const [plans, setPlans] = useState([]);
    const [sub, setSub] = useState(null);
    const [loadingKey, setLoadingKey] = useState(null);

    const load = async () => {
        try {
            const [p, s] = await Promise.all([api.get("/plans"), api.get("/subscription")]);
            setPlans(p.data);
            setSub(s.data);
        } catch (e) { /* session-expired handled globally */ }
    };
    useEffect(() => { load(); }, []);

    const subscribe = async (lookup_key) => {
        setLoadingKey(lookup_key);
        try {
            const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not start checkout");
            setLoadingKey(null);
        }
    };

    const openPortal = async () => {
        setLoadingKey("portal");
        try {
            const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
            window.location.href = data.portal_url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not open billing portal");
            setLoadingKey(null);
        }
    };

    const priceOf = (k) => plans.find((p) => p.lookup_key === k)?.amount;
    const active = sub?.active;
    const monthly = priceOf("ares_monthly");
    const yearly = priceOf("ares_yearly");
    const yearlySavings = monthly != null && yearly != null ? monthly * 12 - yearly : null;

    return (
        <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
                <Badge className="bg-ares-orange/15 text-ares-orange border border-ares-orange/40 mb-3">
                    <Sparkles className="w-3 h-3 mr-1.5" /> 14-day free trial
                </Badge>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Upgrade to ARES Pro</h2>
                <p className="text-ares-muted mt-3 max-w-xl mx-auto">Start a 14-day free trial — cancel anytime before it ends. Full access to every mission module.</p>
            </div>

            {active && (
                <div className="mb-6 rounded-xl border border-ares-emerald/40 bg-ares-emerald/10 px-5 py-4 flex items-center gap-3 flex-wrap" data-testid="active-subscription-banner">
                    <ShieldCheck className="w-5 h-5 text-ares-emerald shrink-0" />
                    <p className="text-sm text-ares-bright">
                        You're on the <span className="font-semibold capitalize">{sub.plan}</span> plan
                        {sub.status === "trialing" && <span className="text-ares-muted"> · trial active</span>}.
                    </p>
                    <Button onClick={openPortal} disabled={loadingKey === "portal"} variant="outline"
                        className="ml-auto h-9 border-ares-emerald/40 bg-transparent hover:bg-ares-emerald/10 text-ares-bright" data-testid="manage-billing-btn">
                        {loadingKey === "portal" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Settings className="w-4 h-4 mr-2" /> Manage billing</>}
                    </Button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Monthly */}
                <PlanCard
                    testid="plan-card-monthly"
                    icon={Calendar}
                    title="Pro Monthly"
                    price={priceOf("ares_monthly")}
                    unit="/month"
                    sub="Billed monthly after trial"
                    active={active}
                    loading={loadingKey === "ares_monthly"}
                    onClick={() => subscribe("ares_monthly")}
                    ctaTestid="subscribe-monthly-btn"
                />
                {/* Yearly */}
                <PlanCard
                    testid="plan-card-yearly"
                    icon={Rocket}
                    title="Pro Yearly"
                    price={priceOf("ares_yearly")}
                    unit="/year"
                    sub="≈ 2 months free vs monthly"
                    savings={yearlySavings}
                    highlight
                    active={active}
                    loading={loadingKey === "ares_yearly"}
                    onClick={() => subscribe("ares_yearly")}
                    ctaTestid="subscribe-yearly-btn"
                />
            </div>

            <PlanComparison active={active} />

            <p className="text-center font-mono text-[11px] text-ares-muted mt-6">
                Secure checkout by Stripe · Test card 4242 4242 4242 4242, any future expiry & CVC
            </p>
        </div>
    );
}

function PlanCard({ icon: Icon, title, price, unit, sub, savings, highlight, active, loading, onClick, testid, ctaTestid }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className={`relative rounded-2xl border p-7 ${highlight ? "border-ares-orange/60 bg-ares-dark glow-orange" : "border-ares-border bg-ares-dark/90"}`}
            data-testid={testid}
        >
            {highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-ares-orange text-white text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-full">Best value</span>
            )}
            <div className="flex items-center gap-2.5 mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${highlight ? "bg-ares-orange/20 text-ares-orange" : "bg-ares-panel text-ares-cyan"}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading text-xl font-semibold">{title}</h3>
            </div>
            <div className="flex items-end gap-1 mb-1">
                <span className="font-heading text-4xl font-bold">${price?.toFixed(2) ?? "—"}</span>
                <span className="text-ares-muted mb-1.5">{unit}</span>
            </div>
            <p className="text-xs text-ares-muted mb-3">{sub}</p>
            {savings != null && savings > 0 && (
                <div className="inline-flex items-center gap-1.5 rounded-full bg-ares-emerald/15 border border-ares-emerald/40 px-3 py-1 mb-5" data-testid="annual-savings-badge">
                    <Sparkles className="w-3 h-3 text-ares-emerald" />
                    <span className="text-xs font-mono font-semibold text-ares-emerald">Save ${savings.toFixed(2)}/year</span>
                </div>
            )}

            <ul className="space-y-2.5 mb-7">
                {FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-ares-bright/90">
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${highlight ? "text-ares-orange" : "text-ares-emerald"}`} /> {f}
                    </li>
                ))}
            </ul>

            <Button
                onClick={onClick}
                disabled={loading || active}
                data-testid={ctaTestid}
                className={`w-full h-11 ${highlight ? "bg-ares-orange hover:bg-[#e55a2b] text-white" : "bg-ares-panel hover:bg-ares-border text-ares-bright border border-ares-border"}`}
            >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : active ? "Current plan" : "Start 14-day trial"}
            </Button>
        </motion.div>
    );
}
